import { createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import type { MeDTO } from '@btw/shared';
import { apiHandlers, isApiError } from '../api/client';
import { qk, reauth } from '../api/hooks';
import { Dialog } from '../components/Overlay';
import { Button } from '../components/Button';
import { Field } from '../components/Controls';
import { useI18n } from '../i18n';
import { useSession } from './session';

interface ReauthApi {
  /** Ask the user to confirm with their TOTP code. Resolves true when re-authenticated within 5 minutes. */
  requestReauth: (reason?: string) => Promise<boolean>;
}
const ReauthContext = createContext<ReauthApi | null>(null);

/**
 * High-risk actions (authorisation, deleting documents, cancelling) require a fresh second factor within
 * 5 minutes (PRD NFR, §9.3). The API answers `reauth_required`; the client calls this provider and retries.
 */
export function ReauthProvider({ children }: { children: ReactNode }) {
  const { t, fmt } = useI18n();
  const { user } = useSession();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<string | undefined>();
  const [code, setCode] = useState('');
  // Lost authenticator: a recovery code works here too (review 2026-10-29 #2).
  const [recovery, setRecovery] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const pending = useRef<Promise<boolean> | null>(null);

  const requestReauth = useCallback((why?: string) => {
    // Several parallel requests share one dialog.
    if (pending.current) return pending.current;
    setReason(why);
    setCode('');
    setRecovery(false);
    setPassword('');
    setError(null);
    setOpen(true);
    pending.current = new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
    return pending.current;
  }, []);

  useEffect(() => {
    apiHandlers.onReauth = () => requestReauth();
    return () => {
      apiHandlers.onReauth = null;
    };
  }, [requestReauth]);

  const finish = (ok: boolean) => {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = null;
    pending.current = null;
  };

  const usesTotp = !!user?.totpEnabled;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await reauth(usesTotp ? { code: code.trim() } : { password });
      const me = qc.getQueryData<MeDTO>(qk.me);
      if (me) qc.setQueryData<MeDTO>(qk.me, { ...me, reauthValidUntil: res.reauthValidUntil });
      finish(true);
    } catch (err) {
      setError(isApiError(err, 'validation') || isApiError(err, 'totp_required') || isApiError(err) ? t('reauth.wrong') : t('error.generic.what'));
    } finally {
      setBusy(false);
    }
  };

  const until = qc.getQueryData<MeDTO>(qk.me)?.reauthValidUntil;
  return (
    <ReauthContext.Provider value={{ requestReauth }}>
      {children}
      <Dialog
        open={open}
        onClose={() => finish(false)}
        dismissOnBackdrop={false}
        title={
          <span className="row" style={{ gap: 10 }}>
            <ShieldCheck size={22} aria-hidden="true" color="var(--brand)" /> {t('reauth.title')}
          </span>
        }
        description={reason ?? t('reauth.text')}
      >
        <form onSubmit={submit} className="form-grid">
          {usesTotp ? (
            <>
              {recovery ? (
                <Field label={t('reauth.recoveryLabel')} hint={t('reauth.recoveryHint')} error={error}>
                  <input
                    key="recovery"
                    className="input input--code"
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="xxxxx-xxxxx"
                    maxLength={14}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-autofocus
                    required
                  />
                </Field>
              ) : (
                <Field label={t('reauth.code')} hint={t('reauth.codeHint')} error={error}>
                  <input
                    key="totp"
                    className="input input--code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9 ]*"
                    placeholder="123 456"
                    maxLength={7}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-autofocus
                    required
                  />
                </Field>
              )}
              <button
                type="button"
                className="link-button small"
                onClick={() => {
                  setRecovery((r) => !r);
                  setCode('');
                  setError(null);
                }}
              >
                {recovery ? t('reauth.useAuthenticator') : t('reauth.useRecovery')}
              </button>
            </>
          ) : (
            <Field label={t('auth.password')} error={error}>
              <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-autofocus required />
            </Field>
          )}
          {until && new Date(until) > new Date() && <p className="muted small">{t('reauth.validUntil', { time: fmt.time(until) })}</p>}
          <div className="dialog__footer" style={{ marginTop: 8 }}>
            <Button variant="secondary" onClick={() => finish(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={busy} disabled={usesTotp ? code.replace(/[\s-]/g, '').length < (recovery ? 10 : 6) : !password}>
              {t('reauth.confirm')}
            </Button>
          </div>
        </form>
      </Dialog>
    </ReauthContext.Provider>
  );
}

export function useReauth(): ReauthApi {
  const v = useContext(ReauthContext);
  if (!v) throw new Error('useReauth outside ReauthProvider');
  return v;
}
